import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import GlobalNav from '@/components/GlobalNav';

export default function TermsPage() {
  return (
    <>
      <Helmet>
        <title>ScoreCite Terms of Service and Privacy</title>
        <meta name="description" content="ScoreCite Terms of Service and Privacy Policy. Website diagnostic system for structural integrity, visibility and trust signal evaluation." />
        <meta name="robots" content="index, follow" />
      </Helmet>
      
      <GlobalNav />
      
      <div className="min-h-screen bg-[#0A0A0A] py-12 px-4">
        <div className="max-w-3xl mx-auto">

          <div className="bg-[#121212] border border-white/10 rounded-lg p-8 md:p-12">
          <h1 className="text-3xl font-bold text-white mb-2">Terms of Service and Privacy</h1>
          <p className="text-zinc-500 text-sm mb-8">Last updated: 08/02/2026</p>
          
          <div className="prose prose-invert prose-zinc max-w-none space-y-6 text-zinc-300 text-sm leading-relaxed">
            
            <section>
              <h2 className="text-lg font-semibold text-white mt-8 mb-3">About ScoreCite</h2>
              <p>
                ScoreCite is a website diagnostic system that evaluates structural integrity, visibility, trust signals and business alignment of publicly accessible websites within a selected target region.
              </p>
              <p className="mt-3">
                ScoreCite is not a content generator, SEO agency, ranking service or marketing consultancy. The system provides analytical insights and structured reports. It does not guarantee rankings, traffic growth, lead generation or commercial outcomes.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mt-8 mb-3">Eligibility</h2>
              <p>You must be at least 18 years old and legally capable of entering into binding agreements.</p>
              <p className="mt-3">By using ScoreCite, you confirm that you own or are authorized to analyze any domain submitted for audit.</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mt-8 mb-3">Service Description</h2>
              <p>An audit is initiated via the "Full Audit" process and includes:</p>
              <ul className="list-disc list-inside mt-3 space-y-2 text-zinc-400">
                <li>Target domain</li>
                <li>Optional competitor domains (depending on plan)</li>
                <li>Optional path restriction</li>
                <li>Selected target region</li>
                <li>Report output language</li>
              </ul>
              <p className="mt-3">An audit consumes one credit when the report generation phase begins.</p>
              <p className="mt-3">Reports include structural, indexing, content, trust, contact, AI readiness and conversion-related evaluations.</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mt-8 mb-3">Credits, Billing and Refunds</h2>
              <p>1 audit = 1 credit.</p>
              <p className="mt-3">Credits are consumed when the "Generating Report" phase begins.</p>
              <p className="mt-3">Cancellation is possible only during the early crawl/fetch stage.</p>
              <p className="mt-3">If cancellation occurs before AI processing begins, the credit is refunded.</p>
              <p className="mt-3">Once AI analysis has started, credits are non-refundable due to third-party API processing costs.</p>
              <p className="mt-3">Free accounts receive one free credit. Free reports do not include:</p>
              <ul className="list-disc list-inside mt-2 space-y-2 text-zinc-400">
                <li>Action Plan</li>
                <li>PDF export</li>
              </ul>
              <p className="mt-3">Paid plans unlock additional functionality as described on the pricing page.</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mt-8 mb-3">5. Data Processing and Privacy (UK GDPR)</h2>

              <h3 className="text-base font-medium text-white mt-6 mb-2">5.1 Data Collected</h3>
              <p>ScoreCite stores:</p>
              <ul className="list-disc list-inside mt-2 space-y-2 text-zinc-400">
                <li>Account data (email address and authentication credentials)</li>
                <li>Submitted domain URLs</li>
                <li>Optional competitor domain URLs</li>
                <li>Generated reports and audit history</li>
              </ul>
              <p className="mt-3">ScoreCite does not store:</p>
              <ul className="list-disc list-inside mt-2 space-y-2 text-zinc-400">
                <li>IP addresses</li>
                <li>Device fingerprints</li>
                <li>Technical tracking identifiers beyond essential service functionality</li>
              </ul>

              <h3 className="text-base font-medium text-white mt-6 mb-2">5.2 Purpose of Processing</h3>
              <p>Data is processed solely to:</p>
              <ul className="list-disc list-inside mt-2 space-y-2 text-zinc-400">
                <li>Provide access to the platform</li>
                <li>Generate diagnostic reports</li>
                <li>Store audit history</li>
                <li>Manage subscription access</li>
              </ul>
              <p className="mt-3 text-zinc-500">Legal basis: performance of contract.</p>

              <h3 className="text-base font-medium text-white mt-6 mb-2">5.3 Website Content Analysis</h3>
              <p>ScoreCite analyzes publicly accessible content of submitted domains.</p>
              <p className="mt-3">No private, restricted or password-protected data is accessed.</p>

              <h3 className="text-base font-medium text-white mt-6 mb-2">5.4 AI Processing</h3>
              <p>AI (GPT-5) is used exclusively to:</p>
              <ul className="list-disc list-inside mt-2 space-y-2 text-zinc-400">
                <li>Generate narrative diagnostic reports</li>
                <li>Provide responses within the AIO module</li>
              </ul>
              <p className="mt-3">Public website content from submitted domains may be processed for report generation.</p>
              <p className="mt-3">No personal data beyond account data is intentionally submitted for AI generation.</p>

              <h3 className="text-base font-medium text-white mt-6 mb-2">5.5 Data Retention</h3>
              <p>Account and audit data are retained while the account remains active.</p>
              <p className="mt-3">Users may request deletion of their account and associated data at any time.</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mt-8 mb-3">6. User Responsibilities</h2>
              <p>You agree:</p>
              <ul className="list-disc list-inside mt-3 space-y-2 text-zinc-400">
                <li>To submit only domains you have the right to analyze</li>
                <li>Not to use the service for unlawful, harmful or abusive purposes</li>
                <li>Not to attempt to reverse engineer or disrupt the platform</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mt-8 mb-3">7. Accuracy and Limitations</h2>
              <p>ScoreCite provides automated and AI-assisted analysis.</p>
              <p className="mt-3">Reports are based on:</p>
              <ul className="list-disc list-inside mt-2 space-y-2 text-zinc-400">
                <li>Algorithmic metrics</li>
                <li>Structural evaluation</li>
                <li>AI contextual interpretation</li>
              </ul>
              <p className="mt-3">While the system is designed for accuracy, results may contain limitations or interpretation variability. Users remain responsible for independent decision-making.</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mt-8 mb-3">8. Limitation of Liability</h2>
              <p>ScoreCite shall not be liable for:</p>
              <ul className="list-disc list-inside mt-3 space-y-2 text-zinc-400">
                <li>Loss of revenue</li>
                <li>Loss of profits</li>
                <li>Business interruption</li>
                <li>Indirect or consequential damages</li>
              </ul>
              <p className="mt-3">Use of the platform and reliance on reports is at your own discretion.</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mt-8 mb-3">9. Service Availability</h2>
              <p>ScoreCite may update, modify or improve features at any time.</p>
              <p className="mt-3">There is no guaranteed uptime level unless otherwise agreed in writing (Enterprise plan only).</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mt-8 mb-3">10. Termination</h2>
              <p>Users may discontinue use at any time.</p>
              <p className="mt-3">ScoreCite reserves the right to suspend or terminate accounts in cases of:</p>
              <ul className="list-disc list-inside mt-2 space-y-2 text-zinc-400">
                <li>Abuse</li>
                <li>Fraudulent activity</li>
                <li>Breach of these Terms</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mt-8 mb-3">11. Governing Law</h2>
              <p>These Terms are governed by the laws of England and Wales.</p>
            </section>

          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <Link to="/" className="text-blue-400 hover:text-blue-300 text-sm">
            &larr; Back to home
          </Link>
        </div>
      </div>
    </div>
    </>
  );
}
